import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScheduleActionLock } from '@/hook/useScheduleActionLock';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'radix-ui';
import { useAccessMode } from '@/context/AccessModeContext';
import { SmartScheduleEvent, toSmartScheduleKst } from '@/features/smartSchedule/model';
import {
    createQuickMoveDraft, formatQuickMoveClock, moveQuickMoveDraft, QuickMoveDraft, QuickMoveSource,
    resolveQuickMoveCachedEligibility, useQuickMoveEligibility, useQuickMoveMemberCacheRevision,
} from '@/features/scheduleQuickMove';
import { QuickMoveContext } from './QuickMoveContext';
import { QuickMoveSheet } from './QuickMoveSheet';
import { QuickMoveConfirm } from './QuickMoveConfirm';
import './QuickMove.css';

type Props = { children: React.ReactNode; centerId: string; authorization: string; slotEvents: SmartScheduleEvent[]; groupSourceFailed: boolean; enabled?: boolean };

export const QuickMoveProvider = ({ children, centerId, authorization, slotEvents, groupSourceFailed, enabled = true }: Props) => {
    const { isReadOnly: isAccessReadOnly } = useAccessMode();
    const isReadOnly = isAccessReadOnly || !enabled;
    const queryClient = useQueryClient();
    /** 스케줄 동작 잠금(수정) — 메뉴 「시간·날짜 변경」은 컨트롤러를 거치지 않고 시트를 바로 연다. 여기서 묻는다 */
    const menuEditLock = useScheduleActionLock('schedule.edit');
    const memberCacheRevision = useQuickMoveMemberCacheRevision({ queryClient, centerId });
    const [menu, setMenu] = useState<{ event: SmartScheduleEvent; top: number; left: number; onOpenSheet?: () => void } | null>(null);
    const [sheet, setSheet] = useState<{ source: QuickMoveSource; draft: QuickMoveDraft; compact?: boolean } | null>(null);
    const [toast, setToast] = useState<{ message: string } | null>(null);
    const eligibility = useQuickMoveEligibility({ centerId, event: menu?.event, groupSourceFailed, enabled: !!menu && !isReadOnly });
    const menuEligibilityRef = useRef({ event: menu?.event, allowed: eligibility.allowed });
    menuEligibilityRef.current = { event: menu?.event, allowed: eligibility.allowed };
    useEffect(() => { setMenu(null); setSheet(null); setToast(null); }, [centerId, enabled]);
    useEffect(() => {
        if (!toast) return;
        const timer = window.setTimeout(() => setToast(null), 3000);
        return () => window.clearTimeout(timer);
    }, [toast]);
    const openMenu = useCallback((event: SmartScheduleEvent, anchor: HTMLElement, onOpenSheet?: () => void) => {
        const rect = anchor.getBoundingClientRect();
        setMenu({ event, onOpenSheet, top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - 170)), left: Math.max(8, Math.min(rect.left, window.innerWidth - 294)) });
    }, []);
    const controller = useMemo(() => isReadOnly ? null : {
        memberCacheRevision,
        openMenu,
        getDragSource: (event: SmartScheduleEvent) => {
            // 메뉴 상태는 최신 ref로 읽고, 카드 갱신은 회원 캐시 revision으로 알린다.
            if (menuEligibilityRef.current.event === event && !menuEligibilityRef.current.allowed) return null;
            const result = resolveQuickMoveCachedEligibility({ queryClient, centerId, event, isReadOnly, groupSourceFailed });
            return result.allowed ? result.source : null;
        },
        moveDraft: (source: QuickMoveSource, startMinutes: number, date?: string) => moveQuickMoveDraft(createQuickMoveDraft(source), { date, startMinutes }),
        formatClock: formatQuickMoveClock,
        openSheet: (source: QuickMoveSource, draft: QuickMoveDraft) => { setMenu(null); setSheet({ source, draft }); },
        openConfirm: (source: QuickMoveSource, draft: QuickMoveDraft) => { setMenu(null); setSheet({ source, draft, compact: draft.date === createQuickMoveDraft(source).date }); },
    }, [centerId, groupSourceFailed, isReadOnly, memberCacheRevision, openMenu, queryClient]);
    const onSuccess = (draft: QuickMoveDraft) => setToast({ message: `일정을 옮겼어요 (${toSmartScheduleKst(draft.date).format('M월 D일')} ${formatQuickMoveClock(draft.startMinutes)}~${formatQuickMoveClock(draft.endMinutes)})` });
    return (
        <QuickMoveContext.Provider value={controller}>
            {children}
            <Dialog.Root open={!!menu && !isReadOnly} modal={false} onOpenChange={(open) => { if (!open) setMenu(null); }}>
                <Dialog.Portal>
                    <Dialog.Content className="quick-move-menu" style={{ top: menu?.top, left: menu?.left }}>
                        <Dialog.Title className="quick-move-menu-title">{menu?.event.title}</Dialog.Title>
                        <button type="button" disabled={!eligibility.allowed} aria-label="시간·날짜 변경" title={eligibility.allowed ? undefined : eligibility.message}
                            onClick={() => {
                                if (!eligibility.allowed) return;
                                const source = eligibility.source;
                                const onOpenSheet = menu?.onOpenSheet;
                                setMenu(null);
                                // 인증을 통과한 뒤에 보던 상세 창을 닫고 시트를 연다(취소하면 아무것도 안 바뀐다)
                                menuEditLock.run(() => { onOpenSheet?.(); setSheet({ source, draft: createQuickMoveDraft(source) }); });
                            }}>시간·날짜 변경</button>
                        <Dialog.Description role={eligibility.allowed ? undefined : 'tooltip'}>{eligibility.allowed ? '강사와 수업 길이는 그대로 옮겨요.' : eligibility.message}</Dialog.Description>
                        <Dialog.Close className="quick-move-menu-close" aria-label="변경 메뉴 닫기">닫기</Dialog.Close>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
            {sheet && !isReadOnly && (sheet.compact ? <QuickMoveConfirm source={sheet.source} initialDraft={sheet.draft} centerId={centerId} authorization={authorization} slotEvents={slotEvents}
                blockedMessage={groupSourceFailed ? '그룹수업 정보를 불러오지 못해 지금은 바꿀 수 없어요.' : undefined} onClose={() => setSheet(null)} onSuccess={onSuccess}
                onEdit={(draft) => setSheet({ source: sheet.source, draft })} /> : <QuickMoveSheet source={sheet.source} initialDraft={sheet.draft} centerId={centerId} authorization={authorization} slotEvents={slotEvents}
                blockedMessage={groupSourceFailed ? '그룹수업 정보를 불러오지 못해 지금은 바꿀 수 없어요.' : undefined} onClose={() => setSheet(null)}
                onSuccess={onSuccess} />)}
            {toast && <div className="quick-move-toast" role="status" aria-label="일정 변경 완료">{toast.message}</div>}
            {menuEditLock.gate}
        </QuickMoveContext.Provider>
    );
};
